import React from "react";

export const DETAILS_PERIODS = Object.freeze({
  FULL_GAME: "FULL_GAME",
  FIRST_HALF: "FIRST_HALF",
  SECOND_HALF: "SECOND_HALF",
});

export const DEFAULT_DETAILS_PERIOD = DETAILS_PERIODS.FULL_GAME;

// One contract for future periods. Availability may later be derived per row/sport.
export const INITIAL_DETAILS_PERIOD_OPTIONS = Object.freeze([
  { key: DETAILS_PERIODS.FULL_GAME, label: "FULL GAME", authoritative: true },
  { key: DETAILS_PERIODS.FIRST_HALF, label: "1H", authoritative: false },
  { key: DETAILS_PERIODS.SECOND_HALF, label: "2H", authoritative: false },
]);

const rowPeriod = (row) => String(row?.Period ?? row?.period ?? "").trim().toUpperCase();

export function detailsPeriodOptions(row, sport, relatedRows = []) {
  const advertised = row?.["Supported Periods"] ?? row?.supported_periods ?? row?.SupportedPeriods;
  const supported = new Set(Array.isArray(advertised) ? advertised.map((value) => String(value || "").trim().toUpperCase()) : []);
  supported.add(DETAILS_PERIODS.FULL_GAME);
  return INITIAL_DETAILS_PERIOD_OPTIONS
    .filter(({ key }) => supported.has(key))
    .map((option) => ({ ...option, authoritative: option.key === DETAILS_PERIODS.FULL_GAME || supported.has(option.key) }));
}

export function normalizeDetailsPeriod(requested, options = detailsPeriodOptions()) {
  const keys = new Set(options.map(({ key }) => key));
  return keys.has(requested) ? requested : DEFAULT_DETAILS_PERIOD;
}

export function clearUnsupportedDetailsPeriodFromUrl(options = detailsPeriodOptions()) {
  if (typeof window === "undefined" || !window.location || !window.history) return false;
  const url = new URL(window.location.href);
  const requested = String(url.searchParams.get("period") || url.searchParams.get("detailsPeriod") || "").trim().toUpperCase();
  if (!requested || normalizeDetailsPeriod(requested, options) === requested) return false;
  url.searchParams.delete("period");
  url.searchParams.delete("detailsPeriod");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  return true;
}

export function DetailsPeriodSelector({ period, onChange, options = detailsPeriodOptions(), darkMode = false }) {
  if (options.length < 2) return null;
  return (
    <div style={{ padding: "12px 16px 0", background: darkMode ? "#111827" : "#ffffff" }}>
      <div
        role="group"
        aria-label="Market period"
        style={{
          display: "inline-grid",
          gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
          gap: 3,
          padding: 3,
          borderRadius: 12,
          border: darkMode ? "1px solid rgba(255,255,255,0.14)" : "1px solid #cbd5e1",
          background: darkMode ? "#0b1220" : "#f1f5f9",
          maxWidth: "100%",
        }}
      >
        {options.map((option) => {
          const active = period === option.key;
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(option.key)}
              style={{
                border: 0,
                borderRadius: 9,
                padding: "7px 12px",
                background: active ? (darkMode ? "#e2e8f0" : "#0f172a") : "transparent",
                color: active ? (darkMode ? "#0f172a" : "#ffffff") : (darkMode ? "#cbd5e1" : "#475569"),
                fontSize: 11,
                fontWeight: 950,
                letterSpacing: 0.35,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PeriodUnavailable({ period = DETAILS_PERIODS.FIRST_HALF, reason, propType, darkMode = false }) {
  const label = period === DETAILS_PERIODS.SECOND_HALF ? "2H" : "1H";
  const blocker = {
    title: "Period analysis not yet available",
    detail: "Authoritative period evidence is required before analysis.",
  };
  return (
    <section
      data-testid="period-unavailable"
      style={{
        margin: 16,
        padding: "34px 22px",
        borderRadius: 16,
        border: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid #e2e8f0",
        background: darkMode ? "#0b1220" : "#f8fafc",
        color: darkMode ? "#f8fafc" : "#0f172a",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 950, letterSpacing: 0.6, marginBottom: 8 }}>{label} INTELLIGENCE</div>
      <div style={{ color: darkMode ? "#94a3b8" : "#64748b", lineHeight: 1.5 }}>
        <div style={{ fontWeight: 850 }}>{blocker.title}</div>
        <div style={{ marginTop: 6, fontSize: 12 }}>{blocker.detail}</div>
      </div>
    </section>
  );
}

export function FirstHalfUnavailable(props) {
  return <PeriodUnavailable period={DETAILS_PERIODS.FIRST_HALF} {...props} />;
}
